import React from 'react';
import { Modal, Button, Tooltip, Tag, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import withStyles from 'react-jss';
import { StylesProps } from 'src/library/types';
import { UserDetailsModalStyles } from './UserDetailsModal.styles';
import { getRolesAndPermissions, HubRef } from './UserManagement.api';
import { HUB_SCOPED_ROLES, HUB_OVERRIDING_ROLES } from './UserManagement.constants';

interface IProps extends StylesProps<ReturnType<typeof UserDetailsModalStyles>> {
  isVisible: boolean;
  handleClose: () => void;
  handleEdit: () => void;
  t: (key: string) => string;
  user: {
    userCode: string;
    name: string;
    phoneNumber: string;
    loginUsername: string;
    status: string;
    creationDate: string;
    roles: string[];
    hubs?: HubRef[];
  } | null;
}

const UserDetailsModal = (props: IProps): React.ReactElement => {
  const { classes, isVisible, handleClose, handleEdit, t, user } = props;
  const [rolePermissions, setRolePermissions] = React.useState<Record<string, string[]>>({});

  React.useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const { ROLE_PERMISSIONS } = await getRolesAndPermissions();
        setRolePermissions(ROLE_PERMISSIONS);
      } catch (error) {
        message.error(t('Failed to fetch role permissions. Please try again.'));
      }
    };

    if (isVisible) {
      fetchPermissions();
    }
  }, [isVisible]);

  return (
    <Modal visible={isVisible} onCancel={handleClose} footer={null} width={480} destroyOnClose>
      {user && (
        <>
          <div className={classes.headerRow}>
            <div>{user.userCode}</div>
            <div style={{ color: user.status === 'Active' ? 'green' : 'red' }}>{user.status}</div>
          </div>
          <div className={classes.sectionTitle}>{t('User Details')}</div>
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('Username')}: </span>
            {user.userCode}
          </div>
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('Name')}: </span>
            {user.name}
          </div>
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('Phone Number')}: </span>
            {user.phoneNumber}
          </div>
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('Email')}: </span>
            {user.loginUsername}
          </div>
          <div className={classes.sectionTitle}>{t('User Roles')}</div>
          <table className={classes.rolesTable}>
            <thead>
              <tr>
                <th>{t('Role Name')}</th>
                <th>{t('Permissions')}</th>
              </tr>
            </thead>
            <tbody>
              {user.roles?.map((role) => (
                <tr key={role}>
                  <td>{role}</td>
                  <td style={{ textAlign: 'center' }}>
                    <Tooltip
                      title={rolePermissions[role]?.join(', ') || t('No Permissions')}
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(() => {
            const hubs = user.hubs || [];
            const roles = user.roles || [];
            const isAdmin = roles.some((r) => HUB_OVERRIDING_ROLES.includes(r));
            const isHubScoped = !isAdmin && roles.some((r) => HUB_SCOPED_ROLES.includes(r));
            if (!hubs.length && !isHubScoped) {
              return null;
            }
            return (
              <>
                <div className={classes.sectionTitle}>{t('Assigned Hubs')}</div>
                <div className={classes.detailRow}>
                  {hubs.length ? (
                    hubs.map((h) => (
                      <Tag key={h.id} style={{ marginBottom: 4 }}>
                        {h.code}
                        {h.name ? ` — ${h.name}` : ''}
                      </Tag>
                    ))
                  ) : (
                    <Tag color="blue">{t('All hubs')}</Tag>
                  )}
                </div>
              </>
            );
          })()}
          <div className={classes.createdText}>
            {t('Created')} {user.creationDate}
          </div>
          <div className={classes.buttonRow}>
            <Button type="primary" onClick={handleEdit}>
              {t('Edit')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default withStyles(UserDetailsModalStyles)(UserDetailsModal);
